import { useEffect } from "react";
import { CheckCircle2, CircleAlert, Info, X } from "lucide-react";
import { create } from "zustand";
import { cn } from "../lib/cn";

type ToastTone = "success" | "error" | "info";

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastStore {
  items: ToastItem[];
  push: (tone: ToastTone, message: string) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;

/**
 * A store rather than context, so a data-layer callback can raise a toast without
 * a hook. The previous admin surface accumulated 865 static feedback calls that
 * rendered outside the theme; one owner avoids repeating that.
 */
export const useToastStore = create<ToastStore>((set) => ({
  items: [],
  push: (tone, message) =>
    set((state) => ({ items: [...state.items, { id: nextId++, tone, message }] })),
  dismiss: (id) => set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
}));

export const toast = {
  success: (message: string) => useToastStore.getState().push("success", message),
  error: (message: string) => useToastStore.getState().push("error", message),
  info: (message: string) => useToastStore.getState().push("info", message),
};

const tones: Record<ToastTone, { className: string; icon: typeof Info }> = {
  success: { className: "border-success-500/30 bg-success-500/10 text-success-600", icon: CheckCircle2 },
  error: { className: "border-danger-500/30 bg-danger-500/10 text-danger-600", icon: CircleAlert },
  info: { className: "border-[var(--border-default)] bg-[var(--surface-raised)] text-[var(--text-primary)]", icon: Info },
};

/**
 * Top on a phone so it clears the bottom navigation and the keyboard; bottom
 * right from `sm`.
 */
export function Toaster() {
  const items = useToastStore((state) => state.items);
  return (
    <div className="pointer-events-none fixed inset-x-0 top-2 z-[60] flex flex-col items-center gap-2 px-3 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:top-auto sm:items-end">
      {items.map((item) => (
        <ToastRow key={item.id} item={item} />
      ))}
    </div>
  );
}

function ToastRow({ item }: { item: ToastItem }) {
  const dismiss = useToastStore((state) => state.dismiss);
  const { className, icon: Icon } = tones[item.tone];

  useEffect(() => {
    const timer = setTimeout(() => dismiss(item.id), 5000);
    return () => clearTimeout(timer);
  }, [item.id, dismiss]);

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-[var(--radius-card)] border px-3 py-2.5 shadow-[var(--shadow-popover)]",
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <p className="flex-1 text-sm">{item.message}</p>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => dismiss(item.id)}
        className="-my-1 -mr-1 grid size-8 shrink-0 place-items-center rounded-[var(--radius-control)] opacity-60 hover:opacity-100"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
